import { PropType } from 'vue';
import { mapState } from 'vuex';
import plantsClient from '@/core/services/plants';
import { Options, Vue } from 'vue-class-component';
import {
  EPDeviceDTO,
  EntrancePlantDetailDTO,
  SwitchboardConfigDTO,
  SwitchboardDTO,
} from '@/../client-generator/generated-clients/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';

@Options({
  name: 'DialogAddEditSwitchboard',
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    switchboardToEdit: {
      type: Object as PropType<SwitchboardDTO>,
      required: false,
    },
  },
  computed:{
    ...mapState(['currentEntrancePlant'])
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (this.isDialogVisible) {
        this.invalidNameReason = '';
        this.isValidName = true;
        this.isValidScs = true;
        this.initSwitchboard();
      }
    },
    'newSwitchboard.switchboard.name': function () {
      if (this.newSwitchboard.switchboard) {
        this.verifyValidityOfName();
      }
    },
    'newSwitchboard.switchboard.scsAddr': function () {
      if (this.newSwitchboard.switchboard) {
        this.verifyValidityOfScsAddress();
      }
    },
  },
  emits: ['hide-dialog', 'update-list'],
})
export default class DialogAddEditSwitchboard extends Vue {
  newSwitchboard: SwitchboardConfigDTO = new SwitchboardConfigDTO();
  currentEntrancePlant!: EntrancePlantDetailDTO;
  switchboardToEdit!: SwitchboardDTO;
  isVisible!: boolean;
  isDialogVisible = false;
  isEdit = false;

  // Naming validation props
  isValidName = true;
  isValidScs = true;
  invalidNameReason: string = '';

  mounted() {
    this.isDialogVisible = this.isVisible;
  }

  async initSwitchboard() {
    if (this.switchboardToEdit) {
      this.isEdit = true;
      this.newSwitchboard.switchboard = new SwitchboardDTO(
        this.switchboardToEdit
      );
      this.newSwitchboard.devices = await this.checkIfSwitchboardHasDevices();
    } else {
      this.isEdit = false;
      this.newSwitchboard.switchboard = new SwitchboardDTO();
      this.newSwitchboard.switchboard.scsAddr = 0;
      this.newSwitchboard.devices = [];
    }
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  get dialogTitle() {
    return this.isEdit
      ? this.$t('AppResourceEP.SwitchboardEditTitle')
      : this.$t('AppResourceEP.SwitchboardAddTitle');
  }

  get buttonSubmitLabel() {
    return this.$t('AppResource.Save');
  }

  get devices() {
    const deviceList = this.fillDeviceList();
    return deviceList;
  }

  get saveButtonDisabled() {
    const disabledForNameCheck =
      !this.isValidName || !this.newSwitchboard.switchboard?.name?.trim();
    const disabledForScsCheck = !this.isValidScs;
    return disabledForNameCheck || disabledForScsCheck;
  }

  fillDeviceList() {
    if (this.currentEntrancePlant.gates === undefined) return [];
    const EPDeviceList: {
      device: EPDeviceDTO;
      gate: string | undefined;
    }[] = [];
    for (const gate of this.currentEntrancePlant.gates) {
      if (gate.devices) {
        for (const device of gate.devices) {
          EPDeviceList.push({
            device: device,
            gate: gate.name,
          });
        }
      }
    }
    return EPDeviceList;
  }

  async checkIfSwitchboardHasDevices() {
    if (this.currentEntrancePlant.gates === undefined) return [];
    const gates = this.currentEntrancePlant.gates;
    let selectedDevices: EPDeviceDTO[] = [];
    for (const gate of gates) {
      if (gate.devices) {
        for (const device of gate.devices) {
          if (
            device.switchboardId ===
            this.newSwitchboard.switchboard?.switchboardId
          ) {
            selectedDevices.push(device);
          }
        }
      }
    }
    return selectedDevices;
  }

  verifyValidityOfName() {
    if (this.newSwitchboard.switchboard?.name === undefined) return;

    this.newSwitchboard.switchboard.name = this.newSwitchboard.switchboard.name.trim();
    let listToCheck = this.currentEntrancePlant.switchboards || [];

    if (this.isEdit) {
      listToCheck = listToCheck.filter(
        (value) => value.switchboardId !== this.switchboardToEdit.switchboardId
      );
    }
    const exist = listToCheck.some(
      (existing) =>
        existing.name?.toLowerCase().trim() ==
        this.newSwitchboard.switchboard?.name?.toLowerCase().trim()
    );
    this.isValidName = !exist;
    this.invalidNameReason = this.isValidName
      ? ''
      : this.$t('general.alreadyExisting');
  }

  verifyValidityOfScsAddress() {
    if (this.newSwitchboard.switchboard?.scsAddr === undefined) return false;

    this.isValidScs = this.newSwitchboard.switchboard.scsAddr >= 0;
  }

  async handleButtonSubmitAction() {
    this.isDialogVisible = false;

    await this.saveSwitchboard();

    this.$emit('update-list');
    this.$toast.add({
      severity: 'success',
      summary: this.isEdit
        ? this.$t('entrancePlant.editedSwitchboard')
        : this.$t('AppResourceEP.SwitchboardBannerCreated'),
      closable: false,
      life: 3000,
    });
    const summary = this.$t('general.synced');
    setTimeout(() => {
      this.$toast.add({
        severity: 'success',
        summary: summary,
        closable: false,
        life: 3000,
      });
    }, 1500);
  }

  async saveSwitchboard() {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      if (this.isEdit) {
        await plantsClient.switchboardPUT(
          this.currentEntrancePlant.desktopId,
          new SwitchboardConfigDTO(this.newSwitchboard)
        );
      } else {
        await plantsClient.switchboardPOST(
          this.currentEntrancePlant.desktopId,
          this.newSwitchboard
        );
      }
    } catch (error) {
      console.log(error);
      setTimeout(() => {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          closable: false,
          life: 3000,
        });
      }, 1500);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }
}
